import { JsonTree } from '../../components/JsonTree';
import { Anchor, Badge, Breadcrumbs, Card, Table, Tabs, Text } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import queryString from 'query-string';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import PreviewHTML from '../../components/PreviewHTML';
import Spinner from '../../components/Spinner';
import dateTimeFormatOptions from '../../constants/dateTimeFormatOptions';
import { Page } from '../../constants/pageTitles';
import useFetch from '../../helpers/useFetch';
import ProductDetailsTab from './ProductDetailsTab';
import ProductInteractionsTab from './ProductInteractionsTab';
import ProductVariantsTab from './ProductVariantsTab';
import RecommendersTab from './RecommendersTab';

const page = `${Page['products']}`;

const tabs = ['product', 'variants', 'interactions', 'recommenders'];

const ProductsDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fetch = useFetch();

  const search = queryString.parse(location.search);
  const tab = search.tab || tabs[0];
  const [product, setProduct] = useState();
  const [recommendations, setRecommendations] = useState();
  const [stats, setStats] = useState();
  const [histogram, setHistogram] = useState();
  const [error, setError] = useState();
  const { accountId, domainId, productId } = useParams();

  const setTab = (tab) => {
    navigate(`${location.pathname}?${queryString.stringify({ ...search, tab })}`);
  };

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/products/${productId}`, {})
      .then(({ product, recommendations }) => {
        setProduct(product);
        setRecommendations(recommendations);
      })
      .catch(setError);
  }, [accountId, domainId, fetch, productId]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/products/${productId}/stats`, {})
      .then(({ stats, histogram }) => {
        setStats(stats);
        setHistogram(histogram);
      })
      .catch(setError);
  }, [accountId, domainId, fetch, productId]);

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (product === undefined || recommendations === undefined) {
    return (
      <>
        <Spinner />
      </>
    );
  }

  const variants = Array.isArray(product.variants) ? product.variants : [];
  // The ingest pipeline synthesizes a single-entry variants[] for every
  // product (see applyVariantDefaults in product-ingestor.js), so only
  // treat 2+ entries as a genuine variant group worth its own tab.
  const hasVariants = variants.length > 1;

  const productProperties = [];
  let i = 0;
  for (const property in product) {
    if (property === 'variants') {
      continue;
    }
    let humanProperty = property.replace(/([A-Z])/g, ' $1');
    humanProperty = humanProperty.charAt(0).toUpperCase() + humanProperty.slice(1);

    let value = product[property];

    if (property === 'custom') {
      const data = Object.keys(value).reduce((accu, k) => {
        accu[k] = value[k].reduce((accu, v) => {
          if (!accu[v.key]) {
            accu[v.key] = [];
          }
          accu[v.key] = accu[v.key].concat(v.values);
          return accu;
        }, {});
        return accu;
      }, {});
      value = <JsonTree defaultExpanded={true} maxDepth={3} data={data} />;
    } else {
      if (typeof value === 'object' && value !== null) {
        value = <JsonTree data={value} defaultExpanded={true} maxDepth={3} />;
      } else if (typeof value === 'boolean') {
        value = value ? 'yes' : 'no';
      } else if (['createdAt', 'updatedAt', 'publishedAt'].indexOf(property) !== -1 && value) {
        value = new Intl.DateTimeFormat(navigator.language, dateTimeFormatOptions).format(new Date(value));
      } else if (['url', 'imageUrl'].indexOf(property) !== -1) {
        value = (
          <Anchor href={value} target="_blank" rel="noopener noreferrer">
            {value} <IconExternalLink size={14} />
          </Anchor>
        );
      } else if (property === 'description') {
        value = <PreviewHTML htmlPreview={value} />;
      }
    }

    productProperties.push(
      <Table.Tr key={i} data-tour={humanProperty === 'Custom' ? 'productCustomField' : null}>
        <Table.Td>
          <Text fw={600}>{humanProperty}</Text>
        </Table.Td>
        <Table.Td style={{ lineBreak: 'anywhere' }}>{value}</Table.Td>
      </Table.Tr>
    );
    i += 1;
  }

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/products` },
    { title: 'Details' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | ${product.name} | Details`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>
      <Tabs value={tab} onChange={setTab} mb="md" m={0} keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="product">Product Details</Tabs.Tab>
          {hasVariants && (
            <Tabs.Tab
              value="variants"
              rightSection={
                <Badge size="sm" circle variant="filled" color="blue">
                  {variants.length}
                </Badge>
              }
            >
              Variants
            </Tabs.Tab>
          )}
          <Tabs.Tab value="interactions">Product Interactions</Tabs.Tab>
          <Tabs.Tab value="recommenders">Recommenders</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="product">
          <ProductDetailsTab product={product} productProperties={productProperties} />
        </Tabs.Panel>
        {hasVariants && (
          <Tabs.Panel value="variants">
            <ProductVariantsTab variants={variants} currency={product.currency} />
          </Tabs.Panel>
        )}
        <Tabs.Panel value="interactions">
          <ProductInteractionsTab histogram={histogram} stats={stats} />
        </Tabs.Panel>
        <Tabs.Panel value="recommenders">
          <Card withBorder>
            <RecommendersTab recommendations={recommendations} />
          </Card>
        </Tabs.Panel>
      </Tabs>
    </>
  );
};

export default ProductsDetailsPage;
